import { getAuthPort } from './auth';
import { config } from './config';
import { HEADER_PERSONIFICACAO, obterPersonificacao } from './personificacao';
import { trackRequest } from '@/lib/request-progress';

/**
 * Cliente HTTP do PAN.
 *
 * A API do PAN é um hub por usuário — não escopada por tenant — então a única
 * coisa que toda chamada precisa é o `Authorization: Bearer <token>` obtido
 * pela `AuthPort`. Não há mais `X-Tenant-Id`/`tenant`/`grupo_empresarial`
 * (esse último, aliás, nunca foi lido pelo backend).
 *
 * Toda requisição passa pelo contador de progresso ambiente e todo erro vira
 * `PanHttpError`, com mensagem que cita o que falhou — a tela de erro nunca
 * mostra "algo deu errado" genérico (Playbook § Os quatro estados).
 */

export class PanHttpError extends Error {
  readonly status: number;
  readonly detalhe: string;

  constructor(recurso: string, status: number, detalhe: string) {
    super(
      status === 0
        ? `Não foi possível falar com a API do PAN ao carregar ${recurso}.`
        : `A API do PAN respondeu ${status} ao carregar ${recurso}.`,
    );
    this.name = 'PanHttpError';
    this.status = status;
    this.detalhe = detalhe;
  }
}

/**
 * Tenta extrair a mensagem amigável que o backend manda no corpo do erro —
 * `{"message": ...}` (`NotAcceptableError`) ou `{"detail": ...}`
 * (`HTTPException` cru do FastAPI). `.message`/`.detalhe` de `PanHttpError`
 * são genéricos de propósito (usados em toda a app); isto é só pra telas que
 * precisam mostrar o motivo específico (ex.: "já personificada por fulano").
 */
export function mensagemDoErro(erro: unknown): string | null {
  if (!(erro instanceof PanHttpError)) return null;
  try {
    const corpo = JSON.parse(erro.detalhe) as { message?: unknown; detail?: unknown };
    const mensagem = corpo.message ?? corpo.detail;
    return typeof mensagem === 'string' && mensagem.trim() !== '' ? mensagem : null;
  } catch {
    return null;
  }
}

async function headers(contentType: string): Promise<Record<string, string>> {
  const token = await getAuthPort().getToken();
  const personificacao = obterPersonificacao();
  return {
    Accept: 'application/json',
    'Content-Type': contentType,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(personificacao ? { [HEADER_PERSONIFICACAO]: personificacao.instalacaoId } : {}),
  };
}

function buildUrl(path: string, query?: Record<string, unknown>, baseUrl?: string): string {
  // A base pode ser relativa (o proxy de desenvolvimento, `/pan-api[-admin]`);
  // sem a base explícita o construtor de URL rejeita caminho relativo.
  const url = new URL(
    path.startsWith('http') ? path : `${baseUrl ?? config.apiBaseUrl}${path}`,
    window.location.origin,
  );
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (key && value != null && value !== '') url.searchParams.set(key, String(value));
  });
  return url.toString();
}

interface RequestOptions {
  /** Nome legível do recurso, usado na mensagem de erro exibida ao usuário. */
  recurso: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, unknown>;
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Pede ao navegador pra tentar entregar mesmo se a página estiver
   * fechando/recarregando (F5, fechar aba) — sem isso, um `fetch` iniciado no
   * `pagehide` é cancelado antes de sair. Usado só em liberações de "melhor
   * esforço" (ex.: retenção de horário), nunca em leitura.
   */
  keepalive?: boolean;
  /** Sub-app alternativo (ex.: `config.adminBaseUrl`) — padrão é `config.apiBaseUrl`. */
  baseUrl?: string;
  /** Corpo `application/x-www-form-urlencoded` — alguns endpoints admin (ex.:
   * sincronização manual) usam `Form(...)` no FastAPI, não JSON. */
  form?: Record<string, string>;
}

export async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const { recurso, method = 'GET', query, body, signal, keepalive, baseUrl, form } = options;

  return trackRequest(async () => {
    let response: Response;
    try {
      response = await fetch(buildUrl(path, query, baseUrl), {
        method,
        headers: await headers(form ? 'application/x-www-form-urlencoded' : 'application/json'),
        signal,
        keepalive,
        body: form ? new URLSearchParams(form).toString() : body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new PanHttpError(recurso, 0, cause instanceof Error ? cause.message : String(cause));
    }

    if (!response.ok) {
      throw new PanHttpError(recurso, response.status, await response.text().catch(() => ''));
    }

    if (response.status === 204) return undefined as T;

    return (await response.json().catch(() => undefined)) as T;
  });
}
